import { Power, Trash2, Building2, MapPin, Briefcase, Euro } from 'lucide-react';
import { formatDate, formatSalary } from '../../lib/utils';
import type { Job } from '../../types/job';

/**
 * Props for the JobCard component.
 */
interface JobCardProps {
  job: Job; // The job object to display
  onToggleStatus?: (id: number) => void; // Callback for toggling job status
  onDelete?: (id: number) => void; // Callback for deleting the job
  showActions?: boolean; // Flag to show action buttons
}

/**
 * Component to render a job card with job details and action buttons.
 */
export function JobCard({ job, onToggleStatus, onDelete, showActions = false }: JobCardProps) {
  return (
    <div
      className={`card transition-shadow ${!job.active ? 'opacity-60' : ''}`} // Apply opacity if job is inactive
    >
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-semibold text-primary-dark">
            {job.job_title} {/* Job title */}
          </h3>
          {showActions && (
            <div className="flex space-x-2"> {/* Action buttons container */}
              <button
                onClick={() => onToggleStatus?.(job.id)} // Trigger toggle status callback
                className={`p-1.5 rounded-full transition-colors ${
                  job.active
                    ? 'bg-accent-error text-red-800 hover:bg-red-200' // Styles for active job
                    : 'bg-accent-success text-green-800 hover:bg-green-200' // Styles for inactive job
                }`}
                title={job.active ? 'Désactiver l\'offre' : 'Activer l\'offre'} // Button title
              >
                <Power className="w-4 h-4" /> {/* Power icon */}
              </button>
              <button
                onClick={() => onDelete?.(job.id)} // Trigger delete callback
                className="p-1.5 rounded-full bg-accent-error text-red-800 hover:bg-red-200 transition-colors"
                title="Supprimer l'offre" // Button title
              >
                <Trash2 className="w-4 h-4" /> {/* Trash icon */}
              </button>
            </div>
          )}
        </div>
        
        <div className="space-y-3 text-text-secondary"> {/* Job details section */}
          <div className="flex items-center">
            <Building2 className="feature-icon" />
            <div className="flex flex-col">
              <span className="font-medium">{job.business}</span> {/* Business name */}
              {job.business_sector && (
                <span className="text-sm text-text-light">{job.business_sector}</span> // Business sector
              )}
            </div>
          </div>
          <div className="flex items-center">
            <MapPin className="feature-icon" />
            <div className="flex flex-col">
              <span>{job.city} ({job.postal_code})</span> {/* Location */}
              {job.state && (
                <span className="text-sm text-text-light">{job.state}</span> // State
              )}
            </div>
          </div>
          <div className="flex items-center">
            <Briefcase className="feature-icon" />
            <div className="flex flex-col">
              <span>{job.contract}</span> {/* Contract type */}
              {job.required_experience && (
                <span className="text-sm text-text-light">{job.required_experience}</span> // Required experience
              )}
            </div>
          </div>
          {job.salary > 0 && (
            <div className="flex items-center">
              <Euro className="feature-icon" />
              <span>{formatSalary(job.salary)}/an</span> {/* Salary */}
            </div>
          )}
        </div>
        
        <div className="mt-4 flex justify-between items-center text-sm">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            job.active
              ? 'bg-accent-success text-green-800' // Styles for active status
              : 'bg-gray-100 text-gray-800' // Styles for inactive status
          }`}>
            {job.active ? 'Active' : 'Inactive'} {/* Job status */}
          </span>
          <span className="text-text-light">
            Publié le {formatDate(job.created_at)} {/* Publication date */}
          </span>
        </div>
      </div>
    </div>
  );
}

